import { useState } from 'react';
import { getObraImageUrl } from '@/utils/image-utils';
import ObraCard from '@/components/obra/ObraCard';
import { Download, Loader2, ChevronLeft, Trash2 } from 'lucide-react';
import { useOfflineChapters } from '@/hooks/useOfflineChapters';

export default function OfflineTab() {
    const { obrasGrouped, loading, removeChapter } = useOfflineChapters();
    const [selectedObraId, setSelectedObraId] = useState<number | null>(null);
    const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());

    // Convert Map to array of unique works
    const works = Array.from(obrasGrouped.values()).map(chapters => {
        const first = chapters[0];
        return {
            id: first.obraId,
            nome: first.obraName,
            imagem: first.obraImage,
            downloadedCount: chapters.length
        };
    });

    const handleRemoveChapter = async (chapterId: number) => {
        if (removingIds.has(chapterId)) return;
        setRemovingIds(prev => new Set(prev).add(chapterId));
        try {
            await removeChapter(chapterId);
        } finally {
            setRemovingIds(prev => {
                const next = new Set(prev);
                next.delete(chapterId);
                return next;
            });
        }
    };

    if (loading && works.length === 0) {
        return (
            <div className="flex flex-col items-center py-16 text-white">
                <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                <p className="text-gray-300">Carregando baixados...</p>
            </div>
        );
    }

    if (works.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-neutral-900 border border-neutral-800 rounded-2xl">
                <Download className="w-20 h-20 text-gray-500 mb-4 opacity-40" />
                <p className="text-gray-400 text-lg text-center px-4">Você não tem obras baixadas para ler offline.</p>
            </div>
        );
    }

    if (selectedObraId !== null) {
        const chapters = obrasGrouped.get(selectedObraId) || [];
        const workName = chapters[0]?.obraName || 'Obra';

        return (
            <div className="animate-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => setSelectedObraId(null)}
                        className="p-2 bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 rounded-xl text-gray-400 hover:text-white transition-all"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-white uppercase italic">{workName}</h2>
                        <p className="text-xs font-bold text-orange-500 uppercase tracking-widest">{chapters.length} capitulos baixados</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {chapters.map((ch) => (
                        <div
                            key={ch.chapterId}
                            className="flex items-center justify-between gap-4 p-4 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-all group"
                        >
                            <a
                                href={`/offline.html?chapterId=${ch.chapterId}`}
                                className="flex-1 min-w-0 hover:text-orange-500 transition-colors cursor-pointer decoration-none"
                            >
                                <p className="font-bold text-white text-sm">Capítulo {ch.chapterNum}</p>
                                {ch.chapterName && <p className="text-gray-500 text-[10px] uppercase font-bold truncate">{ch.chapterName}</p>}
                            </a>
                            <button
                                onClick={() => handleRemoveChapter(ch.chapterId)}
                                disabled={removingIds.has(ch.chapterId)}
                                className="p-2.5 rounded-lg bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white transition-all disabled:opacity-50 shadow-sm"
                                title="Remover download"
                            >
                                {removingIds.has(ch.chapterId) ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Trash2 size={16} />
                                )}
                            </button>
                        </div>
                    ))}
                </div>
                {chapters.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">Nenhum capítulo disponível para esta obra.</p>
                        <button onClick={() => setSelectedObraId(null)} className="text-orange-500 font-bold mt-4 hover:underline">Voltar para a lista</button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="animate-in fade-in duration-500">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-8">
                {works.map((work) => (
                    <ObraCard
                        key={work.id}
                        obra={{
                            id: work.id,
                            nome: work.nome,
                            imagem: getObraImageUrl(work.id, work.imagem || '', 400) || undefined,
                        }}
                        onClick={() => setSelectedObraId(work.id)}
                    />
                ))}
            </div>
        </div>
    );
}
